import type { Doc } from '@doc';
import { createNodeMutation, insertUnitMutation } from '@doc';
import type { Point } from '@geom';
import { Elements, type EID, type NID } from '@graph';
import { expectDefined } from '@utils';
import type { CommandContext } from './CommandContext';
import { revealBounds } from './zoom.commands';

export interface CreateUnitResult {
  doc: Doc;
  selection: Elements;
  entryPoints: Map<NID, Point>;
  node: NID;
  pan: Point;
  zoom: number;
}

export function createUnit(
  cls: string | undefined,
  docPosition: Point | undefined,
  parent: NID | undefined,
  preserveSelection: boolean,
  context: CommandContext
): CreateUnitResult | undefined {
  let { doc, selection } = context;
  const { layouter } = context;
  let node!: NID;

  const oldDoc = doc;

  try {
    doc = doc.produce('createUnit', draft => {
      const doc = draft as Doc;
      node = createNodeMutation(doc, cls, false, layouter.cache, docPosition, parent);
    });
    doc = layouter.runIncrementalLayout(doc, oldDoc.bounds);
    if (!preserveSelection) {
      selection = Elements.make();
    }
    selection = selection.adding(Elements.make([node]));
    const { pan, zoom } = revealBounds(
      expectDefined(doc.elementsBounds(selection)),
      context
    );
    const entryPoints = new Map<NID, Point>();
    if (docPosition) {
      entryPoints.set(node, docPosition);
    }
    const result = { doc, selection, entryPoints, node, pan, zoom };
    context.update(result, true);
    return result;
  } catch (e) {
    console.error(e);
    return;
  }
}

export interface InsertUnitResult {
  doc: Doc;
  selection: Elements;
  entryPoints: Map<NID, Point>;
  newNode: NID;
  newEdge: EID;
  pan: Point;
  zoom: number;
}

export function insertUnit(
  cls: string | undefined,
  docPosition: Point | undefined,
  edge: EID,
  newNodeParent: NID | undefined,
  context: CommandContext
): InsertUnitResult | undefined {
  let { doc, selection } = context;
  const { layouter } = context;
  let newNode!: NID;
  let newEdge!: EID;

  const oldDoc = doc;

  try {
    doc = doc.produce('insertUnit', draft => {
      const doc = draft as Doc;
      ({ newNode, newEdge } = insertUnitMutation(
        doc,
        cls,
        docPosition,
        edge,
        newNodeParent,
        layouter.cache
      ));
    });
    doc = layouter.runIncrementalLayout(doc, oldDoc.bounds);
    selection = Elements.make([newNode], [newEdge]);
    const { pan, zoom } = revealBounds(
      expectDefined(doc.elementsBounds(selection)),
      context
    );
    const entryPoints = new Map<NID, Point>();
    if (docPosition) {
      entryPoints.set(newNode, docPosition);
    }
    const result = { doc, selection, entryPoints, newNode, newEdge, pan, zoom };
    context.update(result, true);
    return result;
  } catch (e) {
    console.error(e);
    return;
  }
}
